/* eslint-disable react/no-unstable-nested-components */
import classnames from 'classnames';
import React, { memo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import './styles.scss';
import { ProgressLoading, OverlayLoading } from '@/shared/ui';
import { BEAT_HR_TAB_ID } from '@/entities/report';
import FallbackError from '@/features/reload-report-tab';
import { DataEffect, UIEffect } from '@/composers';
import BeatHRModal from './modal';
import WrapReport from './wrapReport';
import SummaryFunction, { SummaryFunctionHandle } from './summaryFunction';
import LeftColumn from './leftColumn';
import useBeatsTab from './useBeatsTab';

export type BeatsTabProps = {
  activeTab: string;
  tabRef?: React.Ref<SummaryFunctionHandle>;
};

/**
 * Renders the Beats and HR tab of the report, gating the full view behind AI processing, IndexedDB load and beat download readiness. Shows a progress loader alongside the summary function until useBeatsTab reports the data is ready, then mounts the store data and UI effects, the tab modals, left column and report body inside an error boundary falling back to FallbackError.
 *
 * @param props - Tab inputs including the active tab, the tab ref exposed to the report shell and the flags forwarded to useBeatsTab.
 * @returns The beats tab content, or a progress loading view while the beat data is still being prepared.
 */
const BeatsTab = ({ activeTab, tabRef }: BeatsTabProps) => {
  const {
    keyRecoil,
    isStartedAiProcessing,
    isShowTabHeader,
    studyId,
    profileId,
    isNotEdit,
    isLoadedIndexDB,
    isCompletedDownloadBeat,
    isTabActive,
    hasVisitTab,
  } = useBeatsTab({ activeTab });

  if (
    !isStartedAiProcessing ||
    !hasVisitTab.current ||
    !isLoadedIndexDB ||
    !isCompletedDownloadBeat
  ) {
    return (
      <>
        <ProgressLoading isStartedAiProcessing={isStartedAiProcessing} />
        <SummaryFunction
          ref={tabRef}
          profileId={profileId}
          studyId={studyId}
          isNotEdit={isNotEdit}
        />
      </>
    );
  }

  return (
    <ErrorBoundary
      fallbackRender={({ error, resetErrorBoundary }) => (
        <FallbackError
          error={error}
          resetErrorBoundary={resetErrorBoundary}
          isShowTabHeader={isShowTabHeader}
        />
      )}
    >
      <div
        id={BEAT_HR_TAB_ID}
        className={classnames('report-summary-tab', isShowTabHeader ? '' : '--hide-tab')}
      >
        <OverlayLoading isVisible={!isTabActive} />
        {isTabActive && (
          <>
            <DataEffect tab={activeTab} keyRecoil={keyRecoil} />
            <UIEffect tab={activeTab} keyRecoil={keyRecoil} />
            <BeatHRModal />
            <SummaryFunction
              ref={tabRef}
              profileId={profileId}
              studyId={studyId}
              isNotEdit={isNotEdit}
            />
            <LeftColumn />
            <WrapReport />
          </>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default memo(BeatsTab);
